/**
 * Cross-platform authenticated track download.
 *
 * Two code paths, chosen at runtime:
 *
 *   DESKTOP  — classic authenticated blob fetch + hidden `<a download>`
 *              click. Filename comes from the backend response's
 *              `Content-Disposition` when available (via the ticket
 *              flow) or from the caller's hint otherwise. Works
 *              everywhere except iOS Safari + iOS Chrome (they refuse
 *              to save a blob URL with the correct name — hence the
 *              second path).
 *
 *   iOS      — same-tab navigation to a single-use "download ticket":
 *                1. POST `/api/tracks/{trackId}/download-ticket` with
 *                   the Bearer JWT. Backend runs the identical auth
 *                   gate as `/tracks/{id}/download` and returns a
 *                   short-lived (~90 s), single-use URL.
 *                2. `window.location.assign(ticketUrl)`. The ticket
 *                   endpoint responds with `Content-Disposition:
 *                   attachment; filename="…"; filename*=UTF-8''…` so
 *                   Safari triggers the native Files-app download and
 *                   the browser stays on the current page (attachment
 *                   responses don't replace the document).
 *
 * We deliberately do NOT open a popup on iOS anymore — the ticket
 * response's `attachment` disposition is the correct native signal.
 * Blob URLs are still never used on iOS: they lose the server filename
 * and Safari's download timer can race the revoke.
 */

import { api } from "@/lib/api";

// A track download is ONE response that carries the whole file, and the server only starts
// replying after it has fetched and tagged the file. The site-wide 20 s request timeout
// therefore cut off any download on a slow connection (or a slow storage read) even though
// nothing was wrong. There is no fixed overall deadline here; we only give up if the
// server never answers, or if data stops arriving once it has started.
const DOWNLOAD_FIRST_BYTE_MS = 180_000; // server builds the whole file before replying
const DOWNLOAD_STALL_MS = 60_000; // once bytes flow, abort if none arrive for this long

export function fetchTrackDownloadBlob(trackId, { firstByteMs = DOWNLOAD_FIRST_BYTE_MS, stallMs = DOWNLOAD_STALL_MS } = {}) {
  const controller = new AbortController();
  let timer;
  const arm = (ms) => {
    clearTimeout(timer);
    timer = setTimeout(() => controller.abort(), ms);
  };
  arm(firstByteMs);
  return api
    .get(`/tracks/${trackId}/download`, {
      responseType: "blob",
      timeout: 0, // no fixed overall limit; the stall watchdog above protects against hangs
      signal: controller.signal,
      onDownloadProgress: () => arm(stallMs),
    })
    .finally(() => clearTimeout(timer));
}

/**
 * Kick off an authenticated track download.
 *
 * @param {object} opts
 * @param {string} opts.trackId  Track ID — required on iOS.
 * @param {() => Promise<{data: Blob}>} [opts.fetchBlob]  Desktop blob
 *   fetch (typically `api.get(url, {responseType:'blob'})`). Required
 *   on desktop; ignored on iOS.
 * @param {string} [opts.filename]  Desktop filename hint.
 * @param {(err: unknown) => (boolean|Promise<boolean>)} [opts.onError]
 *   Called with the underlying error. Return `true` if handled.
 *   Preserves the callers' 403 email-verification handling.
 */
export async function downloadAuthenticatedBlob({ trackId, fetchBlob, filename, onError }) {
  const isIOS = detectIOS();

  if (isIOS) {
    try {
      if (!trackId) throw new Error("trackId is required for iOS downloads");
      const { data } = await api.post(`/tracks/${trackId}/download-ticket`);
      if (!data?.url) throw new Error("ticket response missing url");
      // Same-tab navigation. Because the ticket endpoint responds with
      // `Content-Disposition: attachment`, Safari triggers the native
      // Files-app download prompt without replacing the current page.
      // `assign` is preferred over `href` so it participates in
      // history correctly (users can still hit Back).
      window.location.assign(toAbsoluteApiUrl(data.url));
      return;
    } catch (err) {
      const handled = onError ? await onError(err) : false;
      if (!handled) throw err;
      return;
    }
  }

  // Desktop path — classic hidden-anchor blob download.
  let objectUrl = null;
  try {
    if (!fetchBlob) throw new Error("fetchBlob is required for desktop downloads");
    const res = await fetchBlob();
    const blob = res instanceof Blob ? res : new Blob([res.data]);
    objectUrl = window.URL.createObjectURL(blob);
    anchorDownload(objectUrl, filename);
    setTimeout(() => {
      try { window.URL.revokeObjectURL(objectUrl); } catch (_) {}
    }, 60_000);
  } catch (err) {
    if (objectUrl) {
      try { window.URL.revokeObjectURL(objectUrl); } catch (_) {}
    }
    const handled = onError ? await onError(err) : false;
    if (!handled) throw err;
  }
}

/**
 * Detects iOS (iPhone / iPad / iPod) — including iPadOS 13+ which
 * masquerades as macOS Safari but exposes `navigator.maxTouchPoints > 1`.
 */
function detectIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPadOS 13+ desktop-mode masquerade.
  if (ua.includes("Mac") && (navigator.maxTouchPoints || 0) > 1) return true;
  return false;
}

/**
 * Convert a same-origin API path (like "/api/downloads/ticket/…") to
 * an absolute URL that always points at REACT_APP_BACKEND_URL. This
 * matters when the SPA is served from a different origin than the API
 * (production) — a relative navigation would otherwise 404.
 */
function toAbsoluteApiUrl(path) {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  if (!path) return base || "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

function anchorDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
